/**
 * "Text shape" API for fabric.BoundedText: fits the text inside a shape image instead of the
 * rectangle (see TextShape.js for the mask/layout). Mixed onto fabric.BoundedText.prototype, same
 * pattern as TextPathEditor.js.
 * The persisted source of the shapes is `shapeConfig` (see utils/textShapeUtils.ts, which resolves
 * the selected position); this API only applies an already resolved image url.
 */
const { createMaskFromImage, fitMask, getMaskGuideCanvas } = require('./TextShape');

const textShapeEditor = {
    // Url of the applied shape image or null (rectangle). The mask itself lives in item(0)._shapeMask
    textShape: null,
    // { libraryId?: number, shapeMap: [number, string][] } or null - see TextShapeConfig
    shapeConfig: null,

    /**
     * @param {string} src image url - transparent images use their opaque pixels as the shape,
     *  opaque ones their dark pixels
     * @param {() => boolean} [isCurrent] asked right before applying: a slow image must not land on
     *  top of a shape picked while it was loading (see applyTextShapeOption)
     * @returns {Promise<void>}
     */
    setTextShapeImage(src, isCurrent) {
        return new Promise((resolve, reject) => {
            fabric.util.loadImage(
                src,
                (img, isError) => {
                    if (isError || !img) {
                        reject(new Error('Could not load the text shape image'));
                        return;
                    }
                    // Superseded, not failed: rejecting here would surface an error for a shape the
                    //  user already replaced
                    if (isCurrent && !isCurrent()) {
                        resolve();
                        return;
                    }
                    // loadImage doesn't catch its callback: a throw here would leave the promise pending
                    //  forever, and with it the template load and the loader awaiting it
                    try {
                        const mask = createMaskFromImage(img, src);
                        if (!mask) {
                            reject(new Error('The text shape image has no shape to fit the text into'));
                            return;
                        }
                        this._applyTextShape(mask, src);
                        resolve();
                    } catch (error) {
                        reject(error);
                    }
                },
                null,
                'anonymous'
            );
        });
    },

    clearTextShape() {
        if (!this.textShape) {
            return;
        }
        this.textShape = null;
        this.item(0)._shapeMask = null;
        this.item(0).set('textShape', null);
        this._refreshAfterTextShapeChange();
    },

    /**
     * Multiline is what a shape really needs (the text wraps band by band), kept apart so a text to
     *  shape already has it when it's created, before any shape image is picked. The alignment is
     *  only a default at creation, the user is free to change it afterwards
     */
    applyTextShapeConstraints() {
        this.setMultiline(true);
    },

    _applyTextShape(mask, shapeId) {
        if (!mask) {
            return;
        }

        // Same exclusivity as setTextPath(): allowEdit's growing-box model, path text and vertical
        //  text don't apply once the text flows inside a shape
        // Also undoes everything "Allow Moving Text" changed (position, rotation, forced font size) so
        //  the text is back to a plain bounded text
        if (this.allowEdit || this.oldAllowEdit || this.forcedFontSize || this.textTransformed) {
            if (this._editMode && this.canvas) {
                this._forcedEditMode = false;
                this.fire('deselected');
            }
            this.setAllowEdit(false);
            this.set('oldAllowEdit', false);
            this.forceFontSize(false);
            this.textTransformed = false;
            this.item(0).set('calculateDimensions', false);
            this.resetEditMode?.();
            if (this.editMode) {
                this.setEditedProp?.('allowEdit', 'oldAllowEdit');
            }
        }
        if (this.item(0).textPath) {
            this.setTextPath(null);
        }
        if (this.verticalText) {
            this.setVerticalText(false);
        }
        this.applyTextShapeConstraints();

        this.textShape = shapeId;
        this.item(0)._shapeMask = mask;
        // Always relayouts (OpentypeIText._set), even when two different masks share an id
        this.item(0).set('textShape', shapeId);
        this._refreshAfterTextShapeChange();
    },

    _refreshAfterTextShapeChange() {
        this.setText(this.getText());
        this.item(0).set('dirty', true);
        this.set('dirty', true);
        this.canvas?.requestRenderAll();
    },

    // The shape guide is drawn with the selection borders so it never ends up in the object cache
    //  or in exports, only on screen while the element is selected
    drawBorders(ctx, styleOverride) {
        baseDrawBorders.call(this, ctx, styleOverride);

        const mask = this.item(0)?._shapeMask;
        if (!mask || !this.width || !this.height) {
            return this;
        }

        ctx.save();
        // A throw here would abort the whole canvas render, not just the guide
        try {
            // ctx comes translated to the object's center and rotated (see Object._renderControls), so
            //  what's left is the scale/skew/flip half of the matrix - the same one the text is drawn
            //  with, which is what keeps the guide on top of it
            const transform = fabric.util.qrDecompose(
                fabric.util.multiplyTransformMatrices(this.getViewportTransform(), this.calcTransformMatrix())
            );
            const fit = fitMask(mask, this.width, this.height);
            ctx.transform(...fabric.util.calcDimensionsMatrix(transform));
            // Borders are painted over the object, so a flat fill would hide the text. Multiplying by a
            //  light color tints the empty area and leaves whatever is darker (the text) as it is
            ctx.globalCompositeOperation = 'multiply';
            ctx.drawImage(
                getMaskGuideCanvas(mask),
                -this.width / 2 + fit.offsetX,
                -this.height / 2 + fit.offsetY,
                fit.width,
                fit.height
            );
        } catch (error) {
            console.error(error);
        } finally {
            ctx.restore();
        }
        return this;
    }
};

// Whatever BoundedText resolved before this mixin, so the borders of a text without shape stay as they were
const baseDrawBorders = fabric.BoundedText.prototype.drawBorders;

fabric.util.object.extend(fabric.BoundedText.prototype, textShapeEditor);
